import type { ChatErrorCode } from './types';

/**
 * Every error code the chat endpoints can return. Declared as a Record rather than a
 * Set so that adding a code to the contract fails the build here until it is
 * classified — a new code silently falling through to "generic retryable" is exactly
 * the bug this shape prevents.
 */
export const CHAT_ERROR_CODES: Record<ChatErrorCode, true> = {
    invalid_json: true,
    invalid_request: true,
    unauthorized: true,
    forbidden: true,
    not_found: true,
    service_unavailable: true,
    internal_error: true,
    conversation_stale: true,
    conversation_full: true,
    brief_not_draft: true,
    generation_failed: true,
    write_back_failed: true,
    generation_rate_limited: true,
};

/**
 * Narrows a value to a contract error code.
 *
 * Returns null for anything unrecognised — an aborted request, a network failure, or a
 * gateway's HTML error page. Those are all treated as generic retryable failures, which
 * is the correct default: the send may not have reached the service at all.
 */
export function toChatErrorCode(error: unknown): ChatErrorCode | null {
    if (typeof error !== 'object' || error === null || !('error' in error)) return null;

    const code = (error as { error: unknown }).error;
    if (typeof code !== 'string') return null;

    return Object.prototype.hasOwnProperty.call(CHAT_ERROR_CODES, code)
        ? (code as ChatErrorCode)
        : null;
}
